import React from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText, Trash2 } from 'lucide-react';
import { useDeleteReport, useDownloadReport, useReports } from '../../hooks/useReportGeneration';
import { downloadGeneratedReport } from '../../utils/report-download';
import type { ReportFormat } from '../../types/report.types';

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RecentReportsSection(): React.ReactElement {
  const { data, isLoading } = useReports(5, 0);
  const download = useDownloadReport();
  const deleteReport = useDeleteReport();

  const handleDownload = async (id: string, title: string, format: ReportFormat, dateLabel: string) => {
    try {
      const res = await download.mutateAsync(id);
      if (res.data?.downloadUrl) {
        await downloadGeneratedReport(res.data.downloadUrl, title, format, dateLabel);
      } else if (res.error) {
        window.alert(res.error);
      }
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Download failed');
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (window.confirm(`Delete report "${title}"?`)) {
      await deleteReport.mutateAsync(id);
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6" aria-label="Recent Reports">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Recent Reports</h2>
        {(data?.total ?? 0) > 5 && (
          <Link to="/reporting/history" className="text-sm font-medium text-blue-600 hover:text-blue-700">
            View All
          </Link>
        )}
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading reports…</p>}

      {!isLoading && (data?.items.length ?? 0) === 0 && (
        <p className="text-sm text-gray-500">No reports generated yet.</p>
      )}

      <ul className="space-y-3">
        {data?.items.map((report) => (
          <li
            key={report.id}
            className="flex items-center justify-between rounded-lg border border-gray-100 p-4 hover:bg-gray-50"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FileText className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <p className="font-medium text-gray-900">{report.title}</p>
                <p className="text-sm text-gray-600">
                  {report.createdAtDisplay} • {report.format.toUpperCase()} • {formatFileSize(report.fileSizeBytes)}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                aria-label={`Download ${report.title}`}
                onClick={() =>
                  void handleDownload(report.id, report.title, report.format, report.createdAtDisplay)
                }
                className="rounded p-2 text-gray-400 hover:text-gray-600"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label={`Delete ${report.title}`}
                onClick={() => void handleDelete(report.id, report.title)}
                className="rounded p-2 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
